import { nanoid } from 'nanoid';

import { type GridPosition, type SelectionRange } from '../../../state/types';

import { groupContiguous } from './group-contiguous';
import { reconcileIndices } from './reconcile-indices';
import { reconcileSingleIndex } from './reconcile-single-index';
import { type ReconcileContext } from './types';

/**
 * Collects all selected indices from every range on the primary axis,
 * reconciles them, and groups into contiguous runs.
 *
 * Because indices are pooled across ALL input ranges before grouping,
 * disjoint ranges that become adjacent after a structural change merge
 * naturally into a single range.
 *
 * The first output range preserves the ID of the first input range;
 * any additional ranges generated from splits receive new IDs.
 *
 * @example
 * ```ts
 * // Rows [0, 1, 2] selected, row 'b' (index 1) moved to the end:
 * // prev: ['a', 'b', 'c'] → next: ['a', 'c', 'b']
 * // Row 'a' → 0, 'c' → 1 (contiguous), 'b' → 2 (gap)
 * // Result: two ranges — [0, 1] and [2, 2]
 *
 * // Two disjoint row ranges [1, 2] and [4, 5], gap row 'd' (index 3) deleted:
 * // prev: ['a', 'b', 'c', 'd', 'e', 'f'] → next: ['a', 'b', 'c', 'e', 'f']
 * // Ranges become contiguous → merged into single range [1, 4]
 * ```
 */
export function reconcileAllForAxis({
  ranges,
  mode,
  prevEntries,
  nextEntriesLookup,
  prevFields,
  nextFieldsLookup,
}: ReconcileContext & {
  ranges: SelectionRange[];
  mode: 'row' | 'column';
}): SelectionRange[] {
  const isRow = mode === 'row';
  const allIndices = new Set<number>();

  for (const range of ranges) {
    const a = isRow ? range.anchor.row.index : range.anchor.col.index;
    const b = isRow ? range.lead.row.index : range.lead.col.index;
    const min = Math.min(a, b);
    const max = Math.max(a, b);

    for (let i = min; i <= max; i++) {
      allIndices.add(i);
    }
  }

  const newIndices = reconcileIndices(
    [...allIndices],
    isRow ? prevEntries : prevFields,
    isRow ? nextEntriesLookup : nextFieldsLookup
  );

  const groups = groupContiguous(newIndices);

  const fixedCoord =
    reconcileSingleIndex(
      isRow ? ranges[0].anchor.col.index : ranges[0].anchor.row.index,
      isRow ? prevFields : prevEntries,
      isRow ? nextFieldsLookup : nextEntriesLookup
    ) ?? 0;

  return groups.map((group, i) => {
    const min = group[0];
    const max = group[group.length - 1];

    const anchor: GridPosition = isRow
      ? { row: { index: min }, col: { index: fixedCoord } }
      : { row: { index: fixedCoord }, col: { index: min } };

    const lead: GridPosition = isRow
      ? { row: { index: max }, col: { index: fixedCoord } }
      : { row: { index: fixedCoord }, col: { index: max } };

    return { id: i === 0 ? ranges[0].id : nanoid(), anchor, lead };
  });
}
